import { formatMoney } from "@sylviq/ui-core";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { ActionMenu, type ActionMenuItem } from "@/components/ActionMenu";
import { StatusFilter } from "@/components/StatusFilter";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import { useViewSearch } from "@/lib/view-search";
import {
  acceptQuote,
  createQuotePortalToken,
  declineQuote,
  listQuotes,
  portalQuoteUrl,
  type Quote,
  sendQuote,
} from "@/lib/api";
import { quoteStatusLabel } from "./quoteForm";

function tone(status: string): "success" | "warning" | "danger" | "neutral" {
  const key = status.toLowerCase();
  if (key === "accepted" || key === "converted") return "success";
  if (key === "declined" || key === "expired") return "danger";
  if (key === "sent") return "warning";
  return "neutral";
}

function narrowQuoteStatus(
  value: string,
): "" | "draft" | "sent" | "accepted" | "declined" | "expired" | "converted" {
  switch (value) {
    case "draft":
    case "sent":
    case "accepted":
    case "declined":
    case "expired":
    case "converted":
      return value;
    default:
      return "";
  }
}

type QuoteConfirm = "send" | "accept" | "decline";

function quoteStatusValue(quote: Quote, compact = false) {
  const badge = (
    <Badge
      tone={tone(quote.status)}
      className={compact ? "justify-self-start" : undefined}
    >
      {quoteStatusLabel(quote.status)}
    </Badge>
  );
  if (!quote.converted_invoice_id) return badge;
  return (
    <Link
      to="/invoices/$invoiceId"
      params={{ invoiceId: quote.converted_invoice_id }}
      className={
        compact ? "justify-self-start hover:underline" : "hover:underline"
      }
    >
      {badge}
    </Link>
  );
}

export function QuotesPage() {
  const [quotes, setQuotes] = useState<Quote[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusTab, setStatusTab] = useViewSearch<
    "" | "draft" | "sent" | "accepted" | "declined" | "expired" | "converted"
  >(
    "status",
    ["draft", "sent", "accepted", "declined", "expired", "converted"],
    "",
  );
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [portalLink, setPortalLink] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<
    { id: string; action: QuoteConfirm } | null
  >(null);

  const reload = () => {
    listQuotes()
      .then(setQuotes)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : String(cause)),
      );
  };
  useEffect(reload, []);

  const statusCounts = (quotes ?? []).reduce<Record<string, number>>(
    (counts, quote) => {
      const key = quote.status.toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    },
    {},
  );
  const visibleQuotes = (quotes ?? [])
    .filter((quote) => !statusTab || quote.status.toLowerCase() === statusTab)
    .filter((quote) => {
      const query = search.trim().toLowerCase();
      if (!query) return true;
      return (
        quote.quote_number.toLowerCase().includes(query) ||
        quote.customer_name.toLowerCase().includes(query)
      );
    });

  const runAction = async (id: string, name: QuoteConfirm) => {
    setBusy(`${id}:${name}`);
    setError(null);
    try {
      if (name === "send") await sendQuote(id);
      if (name === "accept") await acceptQuote(id);
      if (name === "decline") await declineQuote(id);
      reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(null);
    }
  };

  const copyPortal = async (id: string) => {
    setBusy(`${id}:portal`);
    try {
      const minted = await createQuotePortalToken(id);
      const url = portalQuoteUrl(minted.token);
      try {
        await navigator.clipboard.writeText(url);
        setPortalLink(`Customer link copied: ${url}`);
      } catch {
        setPortalLink(`Customer link (copy manually): ${url}`);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(null);
    }
  };

  const actionsFor = (quote: Quote): ActionMenuItem[] => {
    const status = quote.status.toLowerCase();
    const disabled = busy !== null;
    return [
      {
        id: "open",
        label: "Open quote",
        to: "/quotes/$quoteId",
        params: { quoteId: quote.id },
      },
      ...(status === "draft"
        ? [
            {
              id: "send",
              label: "Mark as sent",
              onSelect: () => setConfirming({ id: quote.id, action: "send" }),
              disabled,
              busy: busy === `${quote.id}:send`,
              busyLabel: "Saving…",
            },
          ]
        : []),
      ...(status === "sent"
        ? [
            {
              id: "accept",
              label: "Mark accepted",
              title:
                "Use this when the customer accepted the quote outside Accounting",
              onSelect: () =>
                setConfirming({ id: quote.id, action: "accept" }),
              disabled,
              busy: busy === `${quote.id}:accept`,
              busyLabel: "Saving…",
            },
            {
              id: "decline",
              label: "Decline",
              onSelect: () =>
                setConfirming({ id: quote.id, action: "decline" }),
              disabled,
              busy: busy === `${quote.id}:decline`,
              busyLabel: "Saving…",
            },
            {
              id: "portal",
              label: "Copy customer link",
              onSelect: () => void copyPortal(quote.id),
              disabled,
              busy: busy === `${quote.id}:portal`,
              busyLabel: "Copying…",
            },
          ]
        : []),
    ];
  };

  const confirmAction = confirming?.action ?? null;

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Quotes"
        description="Priced offers for customers. Accepted quotes can become draft invoices."
        actions={
          <Button asChild>
            <Link to="/quotes/new">New quote</Link>
          </Button>
        }
      />

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
        </div>
      ) : null}
      {portalLink ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm break-all text-[var(--success)]">
          {portalLink}
        </output>
      ) : null}

      <StatusFilter
        label="Filter quotes"
        value={statusTab}
        onChange={(value) => setStatusTab(narrowQuoteStatus(value))}
        options={[
          { value: "", label: "All", count: (quotes ?? []).length },
          { value: "draft", label: "Draft", count: statusCounts.draft ?? 0 },
          { value: "sent", label: "Sent", count: statusCounts.sent ?? 0 },
          { value: "accepted", label: "Accepted", count: statusCounts.accepted ?? 0 },
          { value: "declined", label: "Declined", count: statusCounts.declined ?? 0 },
          { value: "expired", label: "Expired", count: statusCounts.expired ?? 0 },
          { value: "converted", label: "Converted", count: statusCounts.converted ?? 0 },
        ]}
      />
      <div className="surface mb-3 flex flex-wrap items-end gap-3 rounded-2xl p-4">
        <label
          htmlFor="quotes-search"
          className="grid min-w-40 flex-1 gap-1 text-xs font-medium"
        >
          Search
          <Input
            id="quotes-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Number or customer…"
          />
        </label>
        <Button type="button" variant="secondary" onClick={reload}>
          Refresh
        </Button>
      </div>

      <div
        className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
        data-testid="quotes-table"
      >
        <div className="table-scroll">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes === null ? (
                <TableRow>
                  <TableCell colSpan={6}>Loading…</TableCell>
                </TableRow>
              ) : quotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    No quotes yet — create the first one above.
                  </TableCell>
                </TableRow>
              ) : visibleQuotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    No quotes match these filters.
                  </TableCell>
                </TableRow>
              ) : (
                visibleQuotes.map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell className="font-mono text-xs">
                      <Link
                        to="/quotes/$quoteId"
                        params={{ quoteId: quote.id }}
                        className="hover:underline"
                      >
                        {quote.quote_number}
                      </Link>
                    </TableCell>
                    <TableCell>{quote.customer_name}</TableCell>
                    <TableCell>
                      {new Date(
                        `${quote.expiry_date}T00:00:00`,
                      ).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{quoteStatusValue(quote)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney({
                        cents: quote.total_minor,
                        currency: quote.currency_code ?? "USD",
                      })}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <ActionMenu
                          label="Actions"
                          ariaLabel={`Actions for ${quote.quote_number}`}
                          items={actionsFor(quote)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <div
        className="stack-list grid gap-3 min-[64rem]:hidden"
        data-testid="quotes-stack"
      >
        {quotes === null ? (
          <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
        ) : visibleQuotes.length === 0 ? (
          <p className="text-sm text-[var(--muted-foreground)]">
            {quotes.length === 0
              ? "No quotes yet — create the first one."
              : "No quotes match these filters."}
          </p>
        ) : (
          visibleQuotes.map((quote) => {
            return (
              <article
                key={quote.id}
                className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
                  <Link
                    to="/quotes/$quoteId"
                    params={{ quoteId: quote.id }}
                    className="record-title underline-offset-4 hover:underline"
                  >
                    {quote.quote_number}
                  </Link>
                  <span className="tabular-nums">
                    {formatMoney({
                      cents: quote.total_minor,
                      currency: quote.currency_code ?? "USD",
                    })}
                  </span>
                </div>
                <CardField label="Customer">{quote.customer_name}</CardField>
                <CardField label="Expires">
                  {new Date(
                    `${quote.expiry_date}T00:00:00`,
                  ).toLocaleDateString()}
                </CardField>
                <CardField label="Status">
                  {quoteStatusValue(quote, true)}
                </CardField>
                <div className="flex flex-wrap justify-end pt-3">
                  <ActionMenu
                    label="Actions"
                    ariaLabel={`Actions for ${quote.quote_number}`}
                    items={actionsFor(quote)}
                  />
                </div>
              </article>
            );
          })
        )}
      </div>

      <Dialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={
          confirmAction === "send"
            ? "Mark this quote as sent?"
            : confirmAction === "accept"
              ? "Mark this quote accepted?"
              : "Decline this quote?"
        }
        description={
          confirmAction === "send"
            ? "This changes the quote status so it can be accepted. It does not email the customer — copy the customer link to share it."
            : confirmAction === "accept"
              ? "Use this when the customer accepted the quote outside Accounting. The quote can then be converted to a draft invoice."
              : "The quote will be marked declined and can no longer be accepted or converted."
        }
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirming(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              const pending = confirming;
              setConfirming(null);
              if (!pending) return;
              void runAction(pending.id, pending.action);
            }}
          >
            {confirmAction === "send"
              ? "Mark as sent"
              : confirmAction === "accept"
                ? "Mark accepted"
                : "Decline quote"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
